/**
 * Arche Handoff payloads — CLIENT mirror of the canonical contract in
 * `packages/entities/src/ai-tools/handoff.ts` (the client package cannot
 * import workspace packages, so wire shapes are hand-mirrored here — same
 * convention as ./types.ts / ./agent-types.ts). The kind lists are pinned
 * together by `tests/architecture/arche-handoff-registry.test.ts`.
 *
 * A handoff payload is the ONLY sanctioned way one surface hands work to
 * another arche's UI. Producers: the assistant's `open_arche` prefill
 * (validated server-side against the canonical Zod schema before it ever
 * reaches the client) and the AI window's clickable ref-cards. Consumer:
 * the target arche's `useArcheHandoffIntake` hook — never ad-hoc reads of
 * navigation state (also pinned by the architecture test).
 *
 * `parseArcheHandoffPayload` re-validates on the client because navigation
 * state can be forged by any same-origin code path — treat it as untrusted
 * input, mirror the canonical bounds, reject unknown keys.
 */

export interface HandoffAddress {
  email: string;
  name?: string;
}

/** Open the Email composer pre-filled (or on an existing draft). Never sends. */
export interface EmailComposeHandoff {
  kind: 'email_compose';
  draftMessageId?: string;
  accountId?: string;
  to?: HandoffAddress[];
  cc?: HandoffAddress[];
  bcc?: HandoffAddress[];
  subject?: string;
  body?: string;
  replyToMessageId?: string;
  attachmentDocumentIds?: string[];
}

/** Open the Email arche at a message / thread / folder / named view. */
export interface EmailOpenHandoff {
  kind: 'email_open';
  messageId?: string;
  threadId?: string;
  folderId?: string;
  accountId?: string;
  view?: 'inbox' | 'drafts' | 'scheduled' | 'screener';
}

/**
 * GENERIC document handoff: open a Vault document (by reference) in ANY arche
 * with a registered document intake. The kind describes the PAYLOAD, never a
 * destination — the target arche comes from the open_arche call / openHandoff
 * caller. The receiving arche is the authoritative type gate (it refuses
 * unsupported files with a clear message) and opens the bytes as an UNSAVED
 * COPY — a later Save creates a new Vault document; the source is never
 * mutated.
 */
export interface OpenDocumentHandoff {
  kind: 'open_document';
  documentId: string;
}

/** Signing role a handed-off recipient should take. */
export interface HandoffSigner extends HandoffAddress {
  role?: 'signer' | 'viewer' | 'approver';
}

/**
 * Open Document Sign on a NEW draft envelope seeded from a Vault document,
 * with recipients pre-filled. The user still places fields and presses Send
 * inside Document Sign — a handoff never sends anything.
 *
 * `requestId` is the handoff-OPERATION id: it makes intake exactly idempotent
 * and is the only thing that can correlate a `cancelled` result, where no
 * envelope exists. A dedupe key, never an authentication token.
 */
export interface SignDocumentHandoff {
  kind: 'sign_document';
  requestId: string;
  documentId: string;
  title?: string;
  message?: string;
  signingOrder?: 'parallel' | 'sequential';
  recipients?: HandoffSigner[];
  correlationId?: string;
}

/**
 * The REPLY to a `sign_document` handoff. A POINTER, never the audit record:
 * navigation state is same-origin forgeable, so truth comes from reading the
 * envelope under the consumer's own authorization. `cancelled` structurally
 * cannot carry an `envelopeId` (none exists); the other outcomes always do.
 */
export type SignDocumentResultHandoff =
  | {
      kind: 'sign_document_result';
      outcome: 'cancelled';
      requestId: string;
      correlationId?: string;
    }
  | {
      kind: 'sign_document_result';
      outcome: 'saved_draft' | 'sent';
      requestId: string;
      envelopeId: string;
      correlationId?: string;
    };

export type ArcheHandoffPayload =
  | EmailComposeHandoff
  | EmailOpenHandoff
  | OpenDocumentHandoff
  | SignDocumentHandoff
  | SignDocumentResultHandoff;
export type ArcheHandoffKind = ArcheHandoffPayload['kind'];

/** Must list exactly the canonical kinds (architecture-test pinned). */
export const ARCHE_HANDOFF_KINDS = [
  'email_compose',
  'email_open',
  'open_document',
  'sign_document',
  'sign_document_result',
] as const;

/**
 * Arche-BOUND kinds → their single target (mirror of the canonical map).
 * Generic kinds (open_document) are absent — their target is validated
 * against the intake registry; reply kinds route to the requesting arche.
 */
export const HANDOFF_KIND_TARGET_ARCHE: Partial<Record<ArcheHandoffKind, string>> = {
  email_compose: 'arche_email',
  email_open: 'arche_email',
  sign_document: 'arche_document_sign',
};

/**
 * REPLY kinds (mirror): delivered back to the arche that made the request,
 * never to a registry-listed destination — so they need no intake entry. A
 * plugin's arche id is per-environment, which a hardcoded registry cannot
 * express; the platform routes a reply to the origin it recorded instead.
 *
 * Targeting is therefore not the security control here: the payload is
 * non-authoritative by contract, and a consumer must match it to an
 * OUTSTANDING request by `requestId` and consume it once.
 */
export const HANDOFF_REPLY_KINDS = ['sign_document_result'] as const;

export function isHandoffReplyKind(kind: ArcheHandoffKind): boolean {
  return (HANDOFF_REPLY_KINDS as readonly string[]).includes(kind);
}

export interface ArcheHandoffIntake {
  label: string;
  accepts?: string;
  kinds: ArcheHandoffKind[];
}

/** Handoff-intake registry (mirror): which arches receive which kinds. */
export const ARCHE_HANDOFF_INTAKE: Record<string, ArcheHandoffIntake> = {
  arche_image_editor: {
    label: 'Image Editor',
    accepts: 'raster images (PNG, JPEG, WebP, …), PSD, SVG',
    kinds: ['open_document'],
  },
};

/** Would this payload be accepted by this arche? */
export function handoffTargetsArche(payload: ArcheHandoffPayload, archeId: string): boolean {
  const bound = HANDOFF_KIND_TARGET_ARCHE[payload.kind];
  if (bound) return bound === archeId;
  if (isHandoffReplyKind(payload.kind)) return true;
  return ARCHE_HANDOFF_INTAKE[archeId]?.kinds.includes(payload.kind) ?? false;
}

// ---------------------------------------------------------------------------
// Validation (hand-rolled — no zod in the client). Mirrors the canonical
// schema: strict keys, bounded strings/arrays. Returns null on ANY violation
// — callers treat null as "ignore loudly" (toast + telemetry), never crash.
// ---------------------------------------------------------------------------

const MAX_ID = 64;
const MAX_EMAIL = 320;
const MAX_NAME = 200;
const MAX_SUBJECT = 500;
const MAX_BODY = 20_000;
const MAX_RECIPIENTS = 25;
const MAX_ATTACHMENTS = 10;
const MAX_TITLE = 200;
const MAX_MESSAGE = 2000;
const MAX_CORRELATION = 64;
const MAX_SIGNERS = 20;
const EMAIL_OPEN_VIEWS = new Set(['inbox', 'drafts', 'scheduled', 'screener']);
const SIGNING_ORDERS = new Set(['parallel', 'sequential']);
const SIGNER_ROLES = new Set(['signer', 'viewer', 'approver']);
const RESULT_OUTCOMES = new Set(['cancelled', 'saved_draft', 'sent']);
/** Mirrors the canonical `z.string().uuid()`. */
const UUID_RE = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
/** Mirrors the canonical ENVELOPE_ID — `dse_` + 32 lowercase hex. */
const ENVELOPE_ID_RE = /^dse_[0-9a-f]{32}$/;

function isUuid(v: unknown): v is string {
  return typeof v === 'string' && UUID_RE.test(v);
}

/**
 * Recipient list for `sign_document` — mirrors HANDOFF_ADDRESS plus an
 * optional signing role. Canonical bound is `.min(1).max(20)`, so an EMPTY
 * array is invalid: "hand this over with no recipients" is expressed by
 * omitting the field, not by sending `[]`.
 */
function isSignerList(v: unknown): boolean {
  if (v === undefined) return true;
  if (!Array.isArray(v) || v.length < 1 || v.length > MAX_SIGNERS) return false;
  return v.every((a) => {
    if (typeof a !== 'object' || a === null) return false;
    const keys = Object.keys(a as Record<string, unknown>);
    if (!keys.every((k) => k === 'email' || k === 'name' || k === 'role')) return false;
    const { email, name, role } = a as { email?: unknown; name?: unknown; role?: unknown };
    if (!isBoundedString(email, MAX_EMAIL) || !email.includes('@')) return false;
    if (name !== undefined && !isBoundedString(name, MAX_NAME)) return false;
    return role === undefined || SIGNER_ROLES.has(role as string);
  });
}

/**
 * Non-empty bounded string — mirrors the canonical `z.string().min(1).max(n)`
 * used for ids, addresses, and contact names.
 */
function isBoundedString(v: unknown, max: number): v is string {
  return typeof v === 'string' && v.length >= 1 && v.length <= max;
}

/**
 * Bounded string that MAY be empty — mirrors the canonical
 * `z.string().max(n)` (no `.min(1)`) used for `subject` and `body`.
 *
 * These two fields are deliberately empty-able server-side: "compose to Bob,
 * I'll write it myself" is a valid prefill. Validating them with
 * `isBoundedString` rejected a payload the server had already accepted, so
 * the intake hook silently dropped a legal handoff. Keep the two helpers
 * distinct and match each field to the canonical schema's actual bound —
 * boundary parity is covered by __tests__/handoff.test.ts.
 */
function isBoundedStringAllowingEmpty(v: unknown, max: number): v is string {
  return typeof v === 'string' && v.length <= max;
}

function isOptionalId(v: unknown): boolean {
  return v === undefined || isBoundedString(v, MAX_ID);
}

function isAddressList(v: unknown, max: number): boolean {
  if (v === undefined) return true;
  if (!Array.isArray(v) || v.length > max) return false;
  return v.every((a) => {
    if (typeof a !== 'object' || a === null) return false;
    const keys = Object.keys(a as Record<string, unknown>);
    if (!keys.every((k) => k === 'email' || k === 'name')) return false;
    const { email, name } = a as { email?: unknown; name?: unknown };
    if (!isBoundedString(email, MAX_EMAIL) || !email.includes('@')) return false;
    return name === undefined || isBoundedString(name, MAX_NAME);
  });
}

function hasOnlyKeys(obj: Record<string, unknown>, allowed: readonly string[]): boolean {
  return Object.keys(obj).every((k) => allowed.includes(k));
}

/** Validate an untrusted value into a typed handoff payload, or null. */
export function parseArcheHandoffPayload(value: unknown): ArcheHandoffPayload | null {
  if (typeof value !== 'object' || value === null) return null;
  const obj = value as Record<string, unknown>;

  if (obj.kind === 'email_compose') {
    if (
      !hasOnlyKeys(obj, [
        'kind',
        'draftMessageId',
        'accountId',
        'to',
        'cc',
        'bcc',
        'subject',
        'body',
        'replyToMessageId',
        'attachmentDocumentIds',
      ])
    ) {
      return null;
    }
    if (!isOptionalId(obj.draftMessageId) || !isOptionalId(obj.accountId)) return null;
    if (!isOptionalId(obj.replyToMessageId)) return null;
    if (
      !isAddressList(obj.to, MAX_RECIPIENTS) ||
      !isAddressList(obj.cc, MAX_RECIPIENTS) ||
      !isAddressList(obj.bcc, MAX_RECIPIENTS)
    ) {
      return null;
    }
    // subject/body mirror `z.string().max(n)` — empty is legal (see helper).
    if (obj.subject !== undefined && !isBoundedStringAllowingEmpty(obj.subject, MAX_SUBJECT)) {
      return null;
    }
    if (obj.body !== undefined && !isBoundedStringAllowingEmpty(obj.body, MAX_BODY)) return null;
    if (obj.attachmentDocumentIds !== undefined) {
      const ids = obj.attachmentDocumentIds;
      if (
        !Array.isArray(ids) ||
        ids.length > MAX_ATTACHMENTS ||
        !ids.every((id) => isBoundedString(id, MAX_ID))
      ) {
        return null;
      }
    }
    return obj as unknown as EmailComposeHandoff;
  }

  if (obj.kind === 'email_open') {
    if (!hasOnlyKeys(obj, ['kind', 'messageId', 'threadId', 'folderId', 'accountId', 'view'])) {
      return null;
    }
    if (
      !isOptionalId(obj.messageId) ||
      !isOptionalId(obj.threadId) ||
      !isOptionalId(obj.folderId) ||
      !isOptionalId(obj.accountId)
    ) {
      return null;
    }
    if (obj.view !== undefined && !EMAIL_OPEN_VIEWS.has(obj.view as string)) return null;
    return obj as unknown as EmailOpenHandoff;
  }

  if (obj.kind === 'open_document') {
    if (!hasOnlyKeys(obj, ['kind', 'documentId'])) return null;
    if (!isBoundedString(obj.documentId, MAX_ID)) return null;
    return obj as unknown as OpenDocumentHandoff;
  }

  if (obj.kind === 'sign_document') {
    if (
      !hasOnlyKeys(obj, [
        'kind',
        'requestId',
        'documentId',
        'title',
        'message',
        'signingOrder',
        'recipients',
        'correlationId',
      ])
    ) {
      return null;
    }
    if (!isUuid(obj.requestId)) return null;
    if (!isBoundedString(obj.documentId, MAX_ID)) return null;
    if (obj.title !== undefined && !isBoundedString(obj.title, MAX_TITLE)) return null;
    if (obj.message !== undefined && !isBoundedString(obj.message, MAX_MESSAGE)) return null;
    if (obj.signingOrder !== undefined && !SIGNING_ORDERS.has(obj.signingOrder as string)) {
      return null;
    }
    if (!isSignerList(obj.recipients)) return null;
    if (obj.correlationId !== undefined && !isBoundedString(obj.correlationId, MAX_CORRELATION)) {
      return null;
    }
    return obj as unknown as SignDocumentHandoff;
  }

  if (obj.kind === 'sign_document_result') {
    // Discriminated on `outcome`: a cancellation has no envelope to point at,
    // and the other outcomes must carry one. Mirrors the canonical union so a
    // shape the server would reject cannot be applied on the client either.
    if (!RESULT_OUTCOMES.has(obj.outcome as string)) return null;
    const cancelled = obj.outcome === 'cancelled';
    const allowed = cancelled
      ? ['kind', 'outcome', 'requestId', 'correlationId']
      : ['kind', 'outcome', 'requestId', 'envelopeId', 'correlationId'];
    if (!hasOnlyKeys(obj, allowed)) return null;
    if (!isUuid(obj.requestId)) return null;
    if (
      !cancelled &&
      !(typeof obj.envelopeId === 'string' && ENVELOPE_ID_RE.test(obj.envelopeId))
    ) {
      return null;
    }
    if (obj.correlationId !== undefined && !isBoundedString(obj.correlationId, MAX_CORRELATION)) {
      return null;
    }
    return obj as unknown as SignDocumentResultHandoff;
  }

  return null;
}

// ---------------------------------------------------------------------------
// Ref-card registry: which tool-result ref types are openable, and the
// handoff payload a click produces. Adding an entry here is how an arche's
// refs become clickable cards in the AI window.
// ---------------------------------------------------------------------------

export interface RefLike {
  id: string;
  type: string;
  label: string;
}

/** Build the click-through handoff for a tool-result ref, or null (not openable). */
export function refOpenHandoff(ref: RefLike): ArcheHandoffPayload | null {
  if (!isBoundedString(ref.id, MAX_ID)) return null;
  switch (ref.type) {
    case 'email_message':
      return { kind: 'email_open', messageId: ref.id };
    case 'email_thread':
      return { kind: 'email_open', threadId: ref.id };
    case 'email_draft':
      return { kind: 'email_compose', draftMessageId: ref.id };
    case 'email_scheduled_send':
      return { kind: 'email_open', view: 'scheduled' };
    default:
      return null;
  }
}
